import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { KpiMetricCard } from "@/components/symin/KpiMetricCard";
import { IntelligenceScoreGauge } from "@/components/symin/IntelligenceScoreGauge";
import { ProjectMiniMap } from "@/components/symin/ProjectMiniMap";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MapPin, Envelope, PaperPlaneRight, CheckCircle } from "@phosphor-icons/react";

const PRIO = { "Priority 1": "#C20511", "Priority 2": "#FBBF24", "Priority 3": "#94A3B8" };

export default function InvestorSnapshot() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", company: "", message: "" });
  const [leadState, setLeadState] = useState("idle");

  const submitLead = async () => {
    if (!lead.name.trim() || !lead.email.includes("@")) return;
    setLeadState("sending");
    try { await api.post(`/public/investor/${token}/lead`, lead); setLeadState("sent"); }
    catch (e) { setLeadState("idle"); }
  };

  useEffect(() => {
    document.documentElement.classList.add("dark");
    (async () => {
      try { const r = await api.get(`/public/investor/${token}`); setD(r.data); }
      catch (e) { setErr(apiError(e)); }
    })();
  }, [token]);

  if (err) return (
    <div className="min-h-screen bg-background text-foreground grid place-items-center px-6">
      <div className="text-center" data-testid="investor-error">
        <div className="font-display font-semibold text-xl mb-2">Summary unavailable</div>
        <p className="text-sm text-muted-foreground max-w-sm">{err}</p>
      </div>
    </div>
  );
  if (!d) return <div className="min-h-screen bg-background grid place-items-center text-muted-foreground">Loading technical summary…</div>;

  const k = d.kpis || {};
  const project = { areas: d.areas || [] };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="investor-snapshot">
      {/* Brand bar */}
      <header className="h-14 border-b border-border bg-card/80 backdrop-blur flex items-center gap-3 px-5 sm:px-8">
        <img src="/symin-logo.png" alt="SYMIN" className="h-8 w-auto object-contain max-w-[160px]" />
        <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">Resource Technical Summary · Read-Only</span>
      </header>

      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-6 space-y-6">
        {/* Title */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight" data-testid="investor-project-name">{d.name}</h1>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-muted-foreground">
              {d.commodity && <span><span className="text-muted-foreground/60">Commodity</span> · <span className="text-foreground">{d.commodity}</span></span>}
              {d.country && <span><span className="text-muted-foreground/60">Country</span> · <span className="text-foreground">{d.country}</span></span>}
              {d.licence_area ? <span><span className="text-muted-foreground/60">Licence area</span> · <span className="text-foreground">{d.licence_area.toLocaleString()} km²</span></span> : null}
              {d.stage && <span><span className="text-muted-foreground/60">Stage</span> · <span className="text-foreground">{d.stage}</span></span>}
            </div>
          </div>
          <Dialog open={leadOpen} onOpenChange={(o) => { setLeadOpen(o); if (!o) setLeadState("idle"); }}>
            <DialogTrigger asChild>
              <Button className="shrink-0" data-testid="investor-lead-open"><Envelope size={16} className="mr-1.5" /> Request full data room</Button>
            </DialogTrigger>
            <DialogContent data-testid="investor-lead-dialog">
              <DialogHeader><DialogTitle>Request the full data room</DialogTitle></DialogHeader>
              <DialogDescription className="sr-only">Request the full technical data room for this project.</DialogDescription>
              {leadState === "sent" ? (
                <div className="py-6 text-center" data-testid="investor-lead-sent">
                  <CheckCircle size={40} className="mx-auto mb-3" style={{ color: "#10B981" }} weight="fill" />
                  <p className="text-sm text-foreground font-medium">Request sent</p>
                  <p className="text-xs text-muted-foreground mt-1">The project team has been notified and will be in touch shortly.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">Interested in {d.name}? Send your details and the project team will share the full technical data room.</p>
                  <input data-testid="investor-lead-name" placeholder="Your name *" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:border-primary" />
                  <input data-testid="investor-lead-email" type="email" placeholder="Email *" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:border-primary" />
                  <input data-testid="investor-lead-company" placeholder="Company / organisation (optional)" value={lead.company} onChange={(e) => setLead({ ...lead, company: e.target.value })} className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:border-primary" />
                  <textarea data-testid="investor-lead-message" rows={3} placeholder="Message (optional)" value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground resize-none focus:outline-none focus:border-primary" />
                  <Button className="w-full" onClick={submitLead} disabled={leadState === "sending" || !lead.name.trim() || !lead.email.includes("@")} data-testid="investor-lead-submit">
                    <PaperPlaneRight size={15} className="mr-1.5" /> {leadState === "sending" ? "Sending…" : "Send request"}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiMetricCard testid="investor-kpi-targets" title="Exploration Targets" value={k.targets} accent="#C20511" sub={`${k.high_priority || 0} Priority-1`} />
          <KpiMetricCard testid="investor-kpi-prospectivity" title="Max Prospectivity" value={k.max_prospectivity} unit="%" accent="#E67332" sub="Highest ranked target" />
          <KpiMetricCard testid="investor-kpi-areas" title="Licence Areas" value={`${k.areas_complete}/${k.areas}`} accent="#38BDF8" sub="Processed" />
          <KpiMetricCard testid="investor-kpi-confidence" title="Data Confidence" value={k.data_confidence} unit="%" accent="#10B981" sub={`Coverage ${k.avg_coverage || 0}%`} />
        </div>

        {/* Gauge + map */}
        <div className="grid lg:grid-cols-3 gap-5">
          <IntelligenceScoreGauge testid="investor-gauge" score={k.intelligence} />
          <div className="lg:col-span-2 rounded-lg bg-card border border-border overflow-hidden flex flex-col">
            <div className="px-4 py-2.5 border-b border-border text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5"><MapPin size={13} /> Licence Areas</div>
            <ProjectMiniMap project={project} className="h-[320px] w-full" />
          </div>
        </div>

        {/* Ranked targets */}
        <div className="rounded-lg bg-card border border-border overflow-hidden" data-testid="investor-targets">
          <div className="px-4 py-3 border-b border-border font-display font-semibold text-sm">Top Exploration Targets</div>
          {(d.top_targets || []).length === 0
            ? <div className="px-4 py-8 text-center text-sm text-muted-foreground">No targets generated yet.</div>
            : (d.top_targets || []).map((t, i) => {
              const pc = PRIO[t.priority] || "#94A3B8";
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/60 last:border-0" data-testid={`investor-target-${i}`}>
                  <span className="font-mono text-xs text-muted-foreground w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-mono font-semibold text-sm text-foreground w-16">{t.label}</span>
                  <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-[3px]" style={{ color: pc, background: `${pc}1F`, border: `1px solid ${pc}59` }}>{(t.priority || "-").replace("Priority ", "P")}</span>
                  <span className="hidden sm:block text-xs text-muted-foreground flex-1 truncate">{t.commodity}{t.deposit_model ? ` · ${t.deposit_model}` : ""}</span>
                  <div className="flex items-center gap-2 ml-auto">
                    <div className="hidden sm:block w-24 h-1.5 rounded-full bg-border overflow-hidden"><div className="h-full rounded-full" style={{ width: `${t.prospectivity}%`, background: "#C20511" }} /></div>
                    <span className="font-mono font-semibold text-sm w-10 text-right" style={{ color: "#C20511" }}>{t.prospectivity}</span>
                  </div>
                </div>
              );
            })}
        </div>

        <p className="text-[11px] text-muted-foreground text-center pt-2 pb-8">
          All targets are conceptual AI-assisted interpretations from satellite remote sensing and require qualified geologist review and field verification before drilling. Not a Mineral Resource or Reserve statement. · Powered by <span className="font-display font-semibold">SYMIN</span>
        </p>
      </div>
    </div>
  );
}
